/* eslint-disable react/jsx-no-useless-fragment */
// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/system';
import { Typography, Button, CircularProgress, Fade, FormControlLabel, Checkbox, Dialog } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { WideLightTooltip } from 'Components/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import moment from 'moment-timezone';
import kmlNotUploaded from 'Assets/kml-location-inactive.png';
import kmlUploaded from 'Assets/kml-location-active.png';
import ErrorIcon from '@mui/icons-material/Error';
import ConfirmProceedDialog from 'Components/ConfirmProceedDialog';
import bbox from '@turf/bbox';
import Countdown from 'react-countdown';
import { useSelector } from 'react-redux';
import { getOrders } from 'Features/order/orderSelectors';
import { RootState } from 'Store';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { SetKmlBBoxAction } from 'Features/kml/kmlActions';
import { ResetImageryCoverageAction, SetImageryCheckedAction, ToggleImageryProcessingAction } from 'Features/imagery/imageryActions';
import { GetNearmapImageryCoverageThunk, CreateImageryRequestThunk } from 'Features/imagery/imageryThunk';
import { PlaceOrderThunk, RemoveEstimateThunk, SubmitCustomOrderThunk } from 'Features/order/orderThunk';
import { ToggleImageryProcessingThunk } from 'Features/project/projectThunk';
import stylesDef from './styles';
import { getFeatureCollection } from 'Features/kml/kmlSelectors';
import CheckImageryCoverageDialog from '../CheckImageryCoverageDialog';

moment.tz.setDefault('America/New_York');

interface ImageryRequestInfoProps {
  setShowCancelDialog: (show: boolean) => void;
}

export const ImageryRequestInfo = ({ setShowCancelDialog }: ImageryRequestInfoProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmProceedDialog, setShowConfirmProceedDialog] = useState(false);
  const [showCoverageDialog, setShowCoverageDialog] = useState(false);
  const [showRequestImageryDialog, setShowRequestImageryDialog] = useState(false);
  const { activeOrder, placedOrder } = useSelector((state: RootState) => getOrders(state));
  const kmlExists = !!(activeOrder?.boundaryFile);
  const { isEstimate, acreage } = activeOrder || {};
  const { project } = useAppSelector((state: RootState) => state.project);
  const featureCollection = useSelector((state: RootState) => getFeatureCollection(state));
  const { imageryRequest, name: projectName, _id: projectId } = project || {};
  const { bbox: bboxCoords, kmls } = useAppSelector((state) => state.kml.present);
  const activeOrderKml = kmls[activeOrder?._id]?.featureCollection;
  const { nearmapCoverageLoading, nearmapCoverage, imageryChecked, imageryProcessing } = useAppSelector((state: RootState) => state.imagery);

  useEffect(() => {
    if (kmlExists && !imageryRequest && activeOrderKml) {
      const kmlBBox = bbox(activeOrderKml);
      dispatch(SetKmlBBoxAction(kmlBBox));
      dispatch(ResetImageryCoverageAction());
    }
  }, [kmlExists, imageryRequest, activeOrderKml]);

  const checkCoverage = async () => {
    setShowCoverageDialog(true);
    dispatch(SetImageryCheckedAction(true));
    await dispatch(GetNearmapImageryCoverageThunk(bboxCoords, featureCollection?.features[0]?.geometry?.coordinates[0]));
  };

  const handleCoverageDialogProceed = () => {
    setShowCoverageDialog(false);
  };

  const requestImagery = () => {
    if (isEstimate) {
      setShowConfirmProceedDialog(true);
    } else {
      setShowRequestImageryDialog(true);
      window?.pendo?.track('Request Imagery', { kml_size: acreage?.toFixed(2), projectId });
    }
  };

  const onOk = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmProceedDialog(false);
    dispatch(RemoveEstimateThunk(activeOrder._id));
    setShowRequestImageryDialog(true);
    window?.pendo?.track('Request Imagery', { kml_size: acreage?.toFixed(2), projectId });
    e.stopPropagation();
  };

  const handleRequestImageryContinue = async () => {
    await dispatch(CreateImageryRequestThunk());
    setShowRequestImageryDialog(false);
  };

  const submitOrder = async () => {
    setIsSubmitting(true);

    await dispatch(PlaceOrderThunk(undefined, undefined, undefined, undefined, true));

    const now = moment(new Date());

    const notification = moment(new Date(imageryRequest.notificationSent)).add(1, 'd');

    const duration = moment.duration(notification.diff(now));

    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes()) % 60;

    window?.pendo?.track('Submit Now', {
      projection: imageryRequest.projection,
      captureDate: moment(imageryRequest.captured).format('LL'),
      timeLeft: `${hours} hours, ${minutes} minutes`,
      projectName,
      totalCredits: activeOrder?.creditAcreage?.toFixed(2),
      imageryCreditCost: activeOrder?.imageryCreditCost?.toFixed(2),
      projectId: activeOrder.ownerProject,
    });
    setIsSubmitting(false);
  };

  let requested; let uploaded; let rejected; let notificationSent;

  if (imageryRequest) {
    ({ requested, uploaded, rejected, notificationSent } = imageryRequest);
  }

  const imageryRequested = requested && !rejected && !uploaded && !notificationSent;
  const imageryNotificationSent = requested && !rejected && uploaded && notificationSent;

  const toggleImageryProcessing = async () => {
    await dispatch(ToggleImageryProcessingThunk());
  };

  // Determine coverage check result for dialog
  const coverageResult = imageryRequest?.status === 'uncovered'
    ? false
    : imageryChecked && !nearmapCoverageLoading
      ? nearmapCoverage
      : null;

  let imageryRequestIcon;
  let imageryRequestTitle;
  let imageryRequestDescription;
  let imageryInfo;
  let imageryRequestAction;

  if (imageryRequested && !isEstimate && !placedOrder) {
    imageryRequestIcon = <ErrorIcon sx={styles.imageryRequestIcon} fontSize="small" />;
    imageryRequestTitle = 'Imagery Requested';
    imageryRequestDescription = 'Make sure you go to checkout and save your estimate so we know exactly what you need.';
    imageryInfo = null;
    imageryRequestAction = (
      <Box sx={styles.processingContainer}>
        <FormControlLabel
          label={<Typography sx={styles.processingLabel}>Begin Processing Immediately</Typography>}
          control={(
            <Checkbox
              color="primary"
              size="small"
              disabled={isEstimate}
              onChange={toggleImageryProcessing}
              checked={imageryRequest.processImmediately}
              sx={{
                root: styles.checkbox,
              }}
            />
          )}
        />
        <WideLightTooltip
          placement="bottom"
          disableHoverListener={isEstimate}
          title={(
            <Box>
              When start processing immediately is selected, you are forfeiting your chance to review the available imagery for your area of interest before placing your order. If you want to review the imagery before kicking off your project, unselect this option.
            </Box>
          )}
        >
          <InfoIcon fontSize="small" />
        </WideLightTooltip>
      </Box>
    );
  } else if (imageryRequested && isEstimate && activeOrder.orderStatus === 0) {
    imageryRequestIcon = <InfoIcon sx={styles.imageryRequestIconUpdate} fontSize="small" />;
    imageryRequestTitle = 'Imagery Requested';
    imageryRequestDescription = `AirWorks has received your request for ${imageryRequest.provider} imagery. You can expect your dataset to be uploaded here in the next 24 hours.`;
    imageryInfo = <Box />;
    imageryRequestAction = (
      <Box sx={styles.imageryRequestActionContainer}>
        {imageryRequest.processImmediately ? <Typography>Processing Immediately</Typography> : <Box />}
        <Button sx={styles.imageryButton && styles.imageryProcessingButton} onClick={() => setShowCancelDialog(true)}>
          Cancel
        </Button>
      </Box>
    );
  } else if (imageryNotificationSent && !placedOrder) {
    imageryRequestIcon = <InfoIcon sx={styles.imageryRequestIconUpdate} fontSize="small" />;
    imageryRequestTitle = 'Order Placed!';
    const delivery = moment.utc(notificationSent).local().add(1, 'd').format('YYYY-MM-DD HH:mm:ss');
    imageryRequestDescription = "Your Project's order will auto-submit in:";
    imageryInfo = (
      <Box sx={styles.imageryInfoContent}>
        <AccessTimeIcon sx={styles.imageryRequestIconUpdate} fontSize="small" />
        <Typography>
          <Countdown
            date={delivery}
            renderer={(props: any) => (
              <span style={styles.timer}>
                {props.hours}
                :
                {props.minutes}
                :
                {props.seconds}
              </span>
            )}
          />
        </Typography>
      </Box>
    );
    imageryRequestAction = (
      <>
        <Button sx={styles.imageryButton} onClick={() => setShowCancelDialog(true)}>
          Cancel
        </Button>
        <Button onClick={submitOrder} sx={styles.submitButton} disabled={isSubmitting}>
          <Fade in={isSubmitting}>
            <CircularProgress size={24} sx={styles.buttonProgress} />
          </Fade>
          <Fade in={!isSubmitting}>
            <span>Submit Now</span>
          </Fade>
        </Button>

      </>
    );
  } else if (imageryNotificationSent && placedOrder) {
    imageryRequestIcon = <InfoIcon sx={styles.imageryRequestIconUpdate} fontSize="small" />;
    imageryRequestTitle = 'Order Placed!';

    imageryRequestDescription = 'Your order was placed on';
    imageryInfo = (
      <Box sx={styles.imageryInfoContent}>
        <AccessTimeIcon sx={styles.imageryRequestIconUpdate} fontSize="small" />
        <Typography>
          {moment.utc(placedOrder.orderDate).local().format('MMMM D, YYYY')}
        </Typography>
      </Box>
    );
  }

  // Render the sidebar coverage status bar
  const renderCoverageStatusBar = () => {
    // Show no-coverage bar for existing uncovered imagery requests
    if (imageryRequest?.status === 'uncovered') {
      return (
        <Box sx={styles.noCoverageStatusBar}>
          <Box sx={styles.coverageStatusContent}>
            <WarningAmberIcon sx={styles.noCoverageIcon} />
            <Typography sx={styles.noCoverageStatusText}>No Imagery Coverage</Typography>
          </Box>
          <Button
            sx={styles.reviewButton}
            variant="text"
            onClick={() => setShowCoverageDialog(true)}
          >
            REVIEW
          </Button>
        </Box>
      );
    }

    if (!imageryChecked || nearmapCoverageLoading) return null;

    if (nearmapCoverage) {
      // Coverage found - green bar with "Imagery Available" + "REQUEST"
      return (
        <Box sx={styles.coverageStatusBar}>
          <Box sx={styles.coverageStatusContent}>
            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
            <Typography sx={styles.coverageStatusText}>Imagery Available</Typography>
          </Box>
          <Button
            sx={styles.coverageActionButton}
            variant="text"
            onClick={requestImagery}
          >
            REQUEST
          </Button>
        </Box>
      );
    }

    // No coverage - red/pink bar with "No Imagery Coverage" + "REVIEW" + "REQUEST"
    return (
      <Box sx={styles.noCoverageStatusBar}>
        <Box sx={styles.coverageStatusContent}>
          <WarningAmberIcon sx={styles.noCoverageIcon} />
          <Typography sx={styles.noCoverageStatusText}>No Imagery Coverage</Typography>
        </Box>
        <Button
          sx={styles.reviewButton}
          variant="text"
          onClick={() => setShowCoverageDialog(true)}
        >
          REVIEW
        </Button>
      </Box>
    );
  };

  return (
    <>
      {!kmlExists && !placedOrder ? (
        <Box sx={styles.kmlUploadInfoGrid}>
          <img style={styles.kmlUploadIcon} src={kmlNotUploaded} alt="no kml uploaded" />
          <Typography sx={styles.kmlUploadText}>
            Draw or Upload your KML to request imagery from the AirWorks Data Marketplace!
          </Typography>
        </Box>
      ) : (
        <>
          {
            (imageryRequest && !imageryRequest.rejected && imageryRequest.status !== 'uncovered') ? (
              <>
                <Box sx={{ ...styles.imageryRequestCard, ...(isEstimate && styles.imageryRequestBarUpdate) }}>
                  <Box>
                    <Typography sx={styles.imageryRequestTitle}>
                      {imageryRequestIcon}
                      {imageryRequestTitle}
                    </Typography>
                    <Typography sx={styles.imageryRequestDetails}>
                      {imageryRequestDescription}
                    </Typography>
                    <Box sx={styles.imageryBottomContent}>
                      {imageryInfo}
                      {imageryRequestAction}
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {/* Check Imagery Coverage button - shown before check or while loading, hidden for uncovered requests */}
                {(!imageryChecked || nearmapCoverageLoading) && !imageryRequest && (
                  <Box sx={styles.checkCoverageButtonWrapper}>
                    <Button
                      sx={styles.checkCoverageButton}
                      variant="contained"
                      onClick={checkCoverage}
                      disabled={nearmapCoverageLoading}
                      startIcon={<img src={kmlUploaded} alt="" style={{ width: 20, height: 20 }} />}
                      fullWidth
                    >
                      Check Imagery Coverage
                    </Button>
                  </Box>
                )}

                {/* Coverage status bar - shown after check completes */}
                {renderCoverageStatusBar()}
              </>
            )
          }
        </>
      )}
      <ConfirmProceedDialog
        title="Making an Imagery Request will remove your saved estimate and require you to click 'Save Estimate' again after selecting your Projection"
        contentText="Are you sure you want to proceed?"
        showDialog={showConfirmProceedDialog}
        cancelButtonText="CANCEL"
        okButtonText="PROCEED"
        onOk={onOk}
        onCancel={() => setShowConfirmProceedDialog(false)}
      />
      <Dialog open={showRequestImageryDialog} onClose={() => setShowRequestImageryDialog(false)}>
        <Box sx={styles.requestDialogContent}>
          <Typography variant="h3" sx={styles.requestDialogTitle}>Request Imagery</Typography>
          <Typography sx={styles.requestDialogText}>
            Feel free to review{' '}
            <a href="https://www.nearmap.com/us/en/current-aerial-maps-coverage" rel="noreferrer" target="_blank">Nearmap's coverage map</a>
            {' '}and{' '}
            <a href="https://docs.nearmap.com/display/NUB/2020/02/04/Projections+and+Coordinate+Systems+and+the+Importance+of+the+Coordinates" rel="noreferrer" target="_blank">projection system recommendations</a>
            {' '}before proceeding.
          </Typography>
          <Typography sx={styles.requestDialogSubtext}>
            Please continue to checkout and save an estimate so we can kickoff your data upload process.
          </Typography>
          <FormControlLabel
            label={<Typography>Begin Processing Immediately</Typography>}
            control={(
              <Checkbox
                color="primary"
                onChange={() => dispatch(ToggleImageryProcessingAction())}
                checked={imageryProcessing}
              />
            )}
          />
          <Box sx={styles.requestDialogActions}>
            <Button sx={styles.cancelButton} onClick={() => setShowRequestImageryDialog(false)}>CANCEL</Button>
            <Button sx={styles.continueButton} onClick={handleRequestImageryContinue}>CONTINUE</Button>
          </Box>
        </Box>
      </Dialog>
      <CheckImageryCoverageDialog
        open={showCoverageDialog}
        onClose={() => setShowCoverageDialog(false)}
        loading={nearmapCoverageLoading}
        hasCoverage={coverageResult}
        onProceed={handleCoverageDialogProceed}
      />
    </>
  );
};

export default ImageryRequestInfo;
